import { Edit, ImageIcon } from "lucide-react";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import type { LLMModel } from "../api/llm";
import type * as types from "../lib/types";
import { MessageCandidatesModal } from "./message-candidates-modal";
import { ModelSelect } from "./model-select";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

interface ParticipantControlProps {
  participant: types.CharacterInChatRoom | types.Mob;
  type: "character" | "mob";
  isMainCharacter: boolean;
  isGenerating: boolean;
  onUpdateStatus: (messageId: string, status: types.Status) => Promise<void>;
  onSend: (message: string, turnId?: string) => Promise<void>;
  onNameChange: (name: string) => void;
  onNameChangeFinished: (name: string) => Promise<void>;
  onUserMessageChange: (message: string) => void;
  onUserMessageSend: () => Promise<void>;
  onModelChange: (model: LLMModel) => Promise<void>;
  onEditProfile: () => void;
  onEditScene: () => void;
  onFeedbackMessage: (messageId: string, feedback: types.Feedback) => void;
  generateMessageHandler: () => Promise<{ turnId: string; candidates: { id: string; content: string }[] } | undefined>;
  disabled: boolean;
  models: LLMModel[];
  onEditMessage: (messageId: string, content: string) => Promise<void>;
  onUploadImage: () => void;
}

export function ParticipantControl({
  participant,
  type,
  isMainCharacter,
  isGenerating,
  onUpdateStatus,
  onSend,
  onNameChange,
  onNameChangeFinished,
  onUserMessageChange,
  onUserMessageSend,
  onModelChange,
  onEditProfile,
  onEditScene,
  onFeedbackMessage,
  generateMessageHandler,
  disabled,
  models,
  onEditMessage,
  onUploadImage,
}: ParticipantControlProps) {
  const [isMessageCandidatesModalOpen, setIsMessageCandidatesModalOpen] = useState(false);
  const [currentMessages, setCurrentMessages] = useState<
    { turnId: string; candidates: { id: string; content: string }[] } | undefined
  >(undefined);

  const handleGenerate = async () => {
    const messages = await generateMessageHandler();
    if (messages) {
      setCurrentMessages(messages);
      setIsMessageCandidatesModalOpen(true);
    }
  };

  return (
    <div className="flex flex-col h-full border rounded p-2">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-12 h-6 flex items-center justify-center text-xs font-bold rounded bg-gray-200">
          {type === "character" ? (isMainCharacter ? "メイン" : "キャラ") : "モブ"}
        </div>
        <Input
          value={participant.name}
          onChange={(e) => onNameChange(e.target.value)}
          onKeyDown={async (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              await onNameChangeFinished(e.currentTarget.value);
            }
          }}
          placeholder={`${type === "character" ? "キャラクター" : "モブ"}名`}
          className="font-bold"
        />
      </div>
      <ModelSelect value={participant.llm_model} onChange={onModelChange} models={models} />
      <div className="flex-grow overflow-auto my-2">
        {participant.message && (
          <div className="bg-gray-100 rounded p-2 mb-2">
            <p className="text-sm">{participant.message}</p>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 mt-2">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-semibold">プロフィール</h3>
          <Button size="sm" variant="outline" onClick={onEditProfile}>
            <Edit className="w-4 h-4 mr-1" /> 編集
          </Button>
        </div>
      </div>
      {type === "character" && isMainCharacter && (
        <div className="flex flex-col gap-2 mt-2">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-semibold">シーン</h3>
            {isMainCharacter && (
              <Button size="sm" variant="outline" onClick={onEditScene}>
                <Edit className="w-4 h-4 mr-1" /> 編集
              </Button>
            )}
          </div>
        </div>
      )}
      <div className="flex flex-col gap-2 mt-2">
        <div className="flex gap-2">
          <Button onClick={handleGenerate} disabled={disabled || isGenerating} className="flex-grow">
            {isGenerating ? "生成中..." : "次を生成"}
          </Button>
          {participant.message && (
            <Button onClick={() => onSend(participant.message, uuidv4())} disabled={disabled}>
              送信
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Input
            value={participant.userMessage}
            onChange={(e) => onUserMessageChange(e.target.value)}
            placeholder="メッセージを入力"
          />
          <Button onClick={onUploadImage} variant="outline" size="icon">
            <ImageIcon className="h-4 w-4" />
          </Button>
          <Button onClick={onUserMessageSend}>送信</Button>
        </div>
      </div>
      <MessageCandidatesModal
        isOpen={isMessageCandidatesModalOpen}
        onClose={() => setIsMessageCandidatesModalOpen(false)}
        candidates={currentMessages?.candidates ?? []}
        onFeedback={async (message_id, feedback) => {
          onFeedbackMessage(message_id, feedback);
        }}
        onSend={async (message_id) => {
          await onUpdateStatus(message_id, "sent");
          setIsMessageCandidatesModalOpen(false);
        }}
        onSendCustom={async (message) => {
          if (currentMessages?.turnId) {
            await onSend(message, currentMessages.turnId);
            setIsMessageCandidatesModalOpen(false);
          } else {
            console.error("No turnId found");
          }
        }}
        onEdit={onEditMessage}
      />
    </div>
  );
}
